<script setup lang="ts">
import { computed, onErrorCaptured, provide, ref, useId, watch } from 'vue'
import ConfirmOverlay from '~/components/app/ConfirmOverlay.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourcePopin from '~/components/ui/SourcePopin.vue'
import {
  DISCARD_UNSAVED_CANCEL_TEXT,
  DISCARD_UNSAVED_CONFIRM_TEXT,
  DISCARD_UNSAVED_DESCRIPTION,
  DISCARD_UNSAVED_TITLE,
  useDiscardUnsavedGuard,
} from '~/composables/ui/useDiscardUnsavedGuard'

defineOptions({ name: 'OverlayPanel' })

type Props = {
  show?: boolean
  title?: string
  goToHref?: string
  goToLabel?: string
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
  hasClear?: boolean
  hasUnsavedChanges?: boolean
  size?: 'large' | 'xlarge'
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  title: '',
  goToHref: undefined,
  goToLabel: "Voir l'élément",
  closeOnBackdrop: true,
  closeOnEscape: true,
  hasClear: true,
  hasUnsavedChanges: false,
  size: 'large',
  class: undefined,
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const isDirty = computed(() => props.hasUnsavedChanges)

function discardOverlay() {
  emit('close')
}

const {
  showDiscardConfirm,
  requestClose,
  confirmDiscard,
  cancelDiscard,
} = useDiscardUnsavedGuard({
  isDirty,
  onDiscard: discardOverlay,
})

function onGotoClick(e: MouseEvent) {
  e.preventDefault()
  if (props.goToHref) {
    window.open(props.goToHref, '_blank', 'noopener,noreferrer')
  }
}

function onPopinCloseRequest() {
  requestClose()
}

const headerActionsId = `overlay-header-actions-${useId().replace(/:/g, '')}`
provide('monorgaOverlayRequestClose', requestClose)
provide('monorgaOverlayHeaderActionsId', headerActionsId)

const slotError = ref<string | null>(null)
onErrorCaptured((error) => {
  slotError.value = error instanceof Error ? error.message : String(error)
  return false
})

watch(
  () => props.show,
  (open) => {
    if (open) slotError.value = null
  },
)

const popinContentClass = computed((): Array<string | Record<string, boolean>> => {
  if (props.class) return ['overlay-panel', props.class]
  return ['overlay-panel']
})
</script>

<template>
  <SourcePopin
    :open="show"
    :title="title || 'Détail'"
    :size="size"
    :has-clear="hasClear"
    :close-on-backdrop="closeOnBackdrop"
    :close-on-escape="closeOnEscape"
    :content-class="popinContentClass"
    @update:open="(value: boolean) => { if (!value) onPopinCloseRequest() }"
    @close="onPopinCloseRequest"
  >
    <template #header-actions>
      <div :id="headerActionsId" class="overlay-panel__header-actions" />
      <SourceButton
        v-if="goToHref"
        class="overlay-panel__goto"
        type="tertiary"
        size="small"
        icon="open_in_new"
        :text="goToLabel"
        @click="onGotoClick"
      />
    </template>

    <div class="overlay-panel__content">
      <p v-if="slotError" class="overlay-panel__error" role="alert">{{ slotError }}</p>
      <slot v-else />
    </div>
  </SourcePopin>

  <ConfirmOverlay
    :show="showDiscardConfirm"
    :title="DISCARD_UNSAVED_TITLE"
    :description="DISCARD_UNSAVED_DESCRIPTION"
    :confirm-text="DISCARD_UNSAVED_CONFIRM_TEXT"
    :cancel-text="DISCARD_UNSAVED_CANCEL_TEXT"
    variant="warning"
    @close="cancelDiscard"
    @confirm="confirmDiscard"
  />
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/spacing' as *;

.overlay-panel__header-actions {
  display: contents;
}

.overlay-panel__goto {
  flex-shrink: 0;
  white-space: nowrap;
}

.overlay-panel__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.overlay-panel__content > * {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.overlay-panel__error {
  margin: 0;
  color: var(--color-text-primary);
  font-family: Public Sans, sans-serif;
  font-size: 0.875rem;
}
</style>
